import type { SharedTypes } from '@semios/app-platform-banyan-route-definitions'
import type { TInjectorSettings } from 'App/Map/UserSettingsMenu/Shared/FertigationConfiguration/types'
import { translate } from 'i18n/i18n'
import { groupBy } from 'lodash'
import moment from 'moment-timezone'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { isValidIrrigationSchedulerZoneEmitterId } from '../../isValidIrrigationSchedulerZoneEmitterId'
import { mapApiScheduledEventStatusToTScheduledEventEnum } from '../../mapApiScheduledEventStatusToTScheduledEventEnum'
import { irrigationSchedulerStore } from '../irrigationSchedulerStore'
import { getSelectedPropertyTimezoneForIrrigationScheduler } from '../selectors/getSelectedPropertyTimezoneForIrrigationScheduler'

export const eventsCreate = (
  apiEvents: Array<SharedTypes.IrrigationScheduler.IrrigationScheduledEventOutput>,
) => {
  irrigationSchedulerStore.setState((isss) => {
    const fertigationSettingsForProperty = (isss.fertigationSettings ?? []).find(
      (f) => f.propertyId === isss.selectedPropertyId,
    )

    const injectorSettingsByInjectorId =
      fertigationSettingsForProperty?.injectorSettings.reduce<Record<number, TInjectorSettings>>(
        (acc, injector) => {
          acc[injector.tankId] = injector

          return acc
        },
        {},
      ) ?? {}

    const fertilizerNamesByFertilizerId =
      fertigationSettingsForProperty?.fertilizerSettings.reduce<Record<number, string>>((acc, fertilizer) => {
        acc[fertilizer.fertilizerId] = fertilizer.fertilizerName

        return acc
      }, {}) ?? {}

    const timezone = getSelectedPropertyTimezoneForIrrigationScheduler(fieldAssetStore.getState(), isss)

    const updatedScheduledEvents = apiEvents.reduce(
      (acc, apiEvent) => {
        const irrigationZoneEmitterId = apiEvent.irrigationZoneEmitterId

        if (!isValidIrrigationSchedulerZoneEmitterId(irrigationZoneEmitterId)) {
          // eslint-disable-next-line no-console
          console.warn(
            `Invalid irrigationZoneEmitterId: ${irrigationZoneEmitterId} for eventId: ${apiEvent.eventId}`,
          )

          return acc
        }

        const eventId = apiEvent.eventId.toString()

        return {
          ...acc,
          [eventId]: {
            dateFrom: moment.tz(apiEvent.dateFrom, timezone).startOf('minute'),
            dateTo: moment.tz(apiEvent.dateTo, timezone).startOf('minute'),
            emitterType: apiEvent.emitterType,
            irrigationZoneEmitterId,
            irrigationZoneId: apiEvent.irrigationZoneId.toString(),
            eventId: eventId,
            type: mapApiScheduledEventStatusToTScheduledEventEnum(apiEvent.status),
            fertigationSchedules: (apiEvent.fertigationSchedules ?? []).map((fertigationEvent) => {
              const { fertigationStartTime, fertigationEndTime, injectorId, fertilizerId } = fertigationEvent
              const injector = injectorSettingsByInjectorId[injectorId]
              const flowRate = injector?.flowRate
              const flowRateUnit = injector?.flowRateUnit
              const injectorName = injector?.tankName ?? translate.phrases.banyanApp('Unknown Injector') // it's possible tankId can become undefined in property settings if removed in DropControl
              const fertigationStartTimeMoment = moment.tz(fertigationStartTime, timezone).startOf('minute')
              const fertigationEndTimeMoment = moment.tz(fertigationEndTime, timezone).startOf('minute')
              const duration = fertigationEndTimeMoment.diff(fertigationStartTimeMoment, 'minute')

              const fertilizerName =
                fertilizerNamesByFertilizerId[fertilizerId] ??
                translate.phrases.banyanApp('Unknown Fertilizer') // it's possible fertilizerId can become undefined in property settings if removed in DropControl

              return {
                ...fertigationEvent,
                duration,
                fertigationEndTime: fertigationEndTimeMoment,
                fertigationStartTime: fertigationStartTimeMoment,
                fertilizerName,
                flowRate,
                flowRateUnit,
                injectorName,
              }
            }),
          },
        }
      },
      { ...isss.scheduledEvents },
    )

    // IMPORTANT: Keep both flat and grouped structures in sync
    const scheduledEventsArray = Object.values(updatedScheduledEvents)

    const scheduledEventsByIrrigationZoneEmitterId = groupBy(
      scheduledEventsArray,
      (event) => event.irrigationZoneEmitterId,
    )

    return {
      ...isss,
      scheduledEvents: updatedScheduledEvents,
      scheduledEventsByIrrigationZoneEmitterId,
    }
  })
}
